import { z } from 'zod';
import { professionForDisplaySchema } from './IMinimizedProfession';
import { IBaseArea, baseAreaSchema } from './IBaseArea';
import { IBaseFloor, baseFloorSchema } from './IBaseFloor';
import { IssueCommentType } from './ICommentShared';
import { ISelectedPoint } from './ISelectedPoint';
import { dateField } from '@shared/schemas';

export enum IssuePriority {
	CRITICAL = 'critical',
	REGULAR = 'regular',
}

export enum IssueStatus {
	PLANNED = 'planned',
	FOR_REVIEW = 'forReview',
	REJECTED = 'rejected',
	COMPLETED = 'completed',
}

// Schemas (source of truth)
const selectedPointSchema = z.object({
	xPercentage: z.number(),
	yPercentage: z.number(),
});

const areaWithSelectedPointsSchema = baseAreaSchema.extend({
	selectedPoints: z.array(selectedPointSchema).optional(),
});

export const issueLocationSchema = z.object({
	area: areaWithSelectedPointsSchema,
	floor: baseFloorSchema,
});

const userIdentitySchema = z.object({
	name: z.string(),
	username: z.string(),
});

export const issueCommentSchema = z.object({
	commentId: z.string(),
	dateCreated: dateField,
	sender: userIdentitySchema,
	content: z.object({
		type: z.enum(IssueCommentType),
		value: z.string(),
		sizeInBytes: z.number().nullable().optional(),
		filename: z.string().nullable().optional(),
	}),
});
export const issueAssigneeSchema = userIdentitySchema.extend({ name: z.string().optional() });

export const issueSchema = z.object({
	_id: z.string(),
	createdBy: userIdentitySchema.optional(),
	statusUpdateUser: userIdentitySchema.optional(),
	projectId: z.string(),
	status: z.enum(IssueStatus),
	createDate: dateField,
	description: z.string(),
	dueDate: dateField.optional(),
	/**
	 * The DISPLAY shape, not the stored one. `issueSchema` is the response schema
	 * for every issue route, and a response schema that says `minimized` makes the
	 * serializer strip the fields response-enrichment just added — see
	 * `professionForDisplaySchema`. The write side is pinned back to the minimized
	 * shape in `createIssueBodySchema` / `updateIssueBodySchema` so this does not
	 * widen what a client may persist.
	 */
	profession: professionForDisplaySchema.optional(),
	priority: z.enum(IssuePriority),
	locations: z.array(issueLocationSchema).optional(),
	assignee: issueAssigneeSchema.nullable().optional(),
	comments: z.array(issueCommentSchema).optional(),
	statusUpdateDate: dateField.optional(),
	isInitial: z.boolean().optional(),
	linkedAreaSequenceItemId: z.string().nullable().optional(),
	linkedActivityGroupId: z.string().nullable().optional(),
	isPrivate: z.boolean().optional(),
	procoreId: z.union([z.number(), z.string()]).nullable().optional(),
	isSafety: z.boolean().optional(),
});

// Interfaces (kept for backward compatibility)
export interface IAreaWithSelectedPoints extends IBaseArea {
	selectedPoints?: ISelectedPoint[];
}

export interface IIssueLocation {
	area: IAreaWithSelectedPoints;
	floor: IBaseFloor;
}

export type IIssueAssignee = z.infer<typeof issueAssigneeSchema>;

// Types (derived from schema — source of truth)
export type IIssueShared = z.infer<typeof issueSchema>;
